// Profitability (PF1–PF7, PRD §4.16, rev. 1.20): the company's profit by month, looking forward, and
// what switching projects on or off does to it. Earned revenue against every expense, salaried pay
// whether staffed or not and shared costs included, with staffing by month and placeholder hires so
// hiring decisions can be made months ahead. Toggles recompute in the browser (core/profitability.ts).

import { useMemo, useState } from "react";
import {
  Body1,
  Button,
  Caption1,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Select,
  Subtitle2,
  Switch,
  Tab,
  TabList,
  Tooltip,
} from "@fluentui/react-components";
import {
  addMonths,
  compareLines,
  computeProfitability,
  COST_GROUPS,
  defaultOn,
  isOn,
  monthOf,
  newId,
  PROFIT_STATUSES,
  STATUS_LABEL,
  today,
  type PlaceholderHire,
  type ComparedLine,
  type ProfitChoices,
  type ProfitLineGroup,
  type ProfitMonth,
  type ProfitProject,
} from "../../../core";
import { errorText, send, useApi, type CodesResponse, type ProfitabilityResponse } from "../api";
import { delta, hours as fmtHours, money, pct } from "../format";
import { go, href } from "../router";
import { ErrorBar, Kpi, Loading, Page, useMe, Warnings } from "../ui";
import { ProfitChart, ProfitLegend } from "./Charts";
import { CopyDialog } from "./ProjectDialogs";

const LIVE = "";
const DEFAULT_CHOICES: ProfitChoices = { on: {}, weighted: false, basis: "base", hires: [] };
const FLAG_TEXT = { hire: "Hire", rebalance: "Move work", bench: "Bench" } as const;
const FLAG_CLASS = { hire: "load-over", rebalance: "load-over", bench: "load-under" } as const;

/** Only explicit choices that differ from the status default are kept, so a scenario stays small and current. */
const tidy = (c: ProfitChoices, projects: readonly ProfitProject[]): ProfitChoices => ({
  ...c,
  on: Object.fromEntries(Object.entries(c.on).filter(([id, v]) => {
    const p = projects.find((x) => x.id === id);
    return !p || v !== defaultOn(p.status);
  })),
});
const same = (a: ProfitChoices, b: ProfitChoices) => JSON.stringify(a) === JSON.stringify(b);

export function Profitability() {
  const me = useMe();
  const [from, setFrom] = useState(monthOf(today()));
  const data = useApi<ProfitabilityResponse>(`/profitability?from=${from}`);
  const codes = useApi<CodesResponse>(me.can("plans.edit") ? "/project-codes" : null);
  const [choices, setChoices] = useState<ProfitChoices>(DEFAULT_CHOICES);
  const [scenarioId, setScenarioId] = useState(LIVE);
  const [table, setTable] = useState(false);
  // A month picked on Profit by month narrows the breakdown below it to that month (PF8).
  const [month, setMonth] = useState<string | null>(null);
  const [renewing, setRenewing] = useState<ProfitProject | null>(null);
  const [naming, setNaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const d = data.data;
  const result = useMemo(() => (d ? computeProfitability(d, choices) : null), [d, choices]);
  if (!d || !result) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;

  const canSave = me.can("plans.edit") && !me.viewingAs;
  const scenario = d.scenarios.find((s) => s.id === scenarioId) ?? null;
  const tidied = tidy(choices, d.projects);
  const baseline = scenario ? tidy({ on: scenario.on, weighted: scenario.weighted, basis: scenario.basis, hires: scenario.hires }, d.projects) : DEFAULT_CHOICES;
  const changed = !same(tidied, baseline);
  const roleName = (id: string) => d.roles.find((r) => r.id === id)?.name ?? id;
  const byId = new Map(d.projects.map((p) => [p.id, p]));
  const impact = new Map(result.projects.map((p) => [p.id, p]));
  const span = `${d.months[0]} – ${d.months.at(-1)}`;
  const t = result.totals;
  const hireMonths = result.months.filter((m) => m.flag === "hire");

  const set = (change: Partial<ProfitChoices>) => setChoices({ ...choices, ...change });
  const toggle = (p: ProfitProject, on: boolean) => set({ on: { ...choices.on, [p.id]: on } });
  const open = (id: string) => {
    const s = d.scenarios.find((x) => x.id === id);
    setScenarioId(id);
    setChoices(s ? { on: { ...s.on }, weighted: s.weighted, basis: s.basis, hires: s.hires.map((h) => ({ ...h })) } : DEFAULT_CHOICES);
  };

  async function save(asNew: boolean, typed?: string) {
    const name = asNew || !scenario ? typed?.trim() : scenario.name;
    if (!name) return;
    setBusy(true);
    setError(undefined);
    try {
      const reply =
        asNew || !scenario
          ? await send<{ scenario: { id: string } }>("POST", "/profitability/scenarios", { name, choices: tidied })
          : await send<{ scenario: { id: string } }>("PUT", `/profitability/scenarios/${scenario.id}`, { etag: scenario._etag, name, choices: tidied });
      data.reload();
      setScenarioId(reply.scenario.id);
      setNaming(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!scenario || !window.confirm(`Delete the scenario "${scenario.name}"? Projects are not changed.`)) return;
    setBusy(true);
    setError(undefined);
    try {
      await send("DELETE", `/profitability/scenarios/${scenario.id}?etag=${encodeURIComponent(scenario._etag)}`);
      data.reload();
      open(LIVE);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // Projects by status, each renewal listed under the project it renews.
  const renewalsOf = (id: string) => d.projects.filter((p) => p.renewalOf === id);
  const topLevel = (s: string) => d.projects.filter((p) => p.status === s && !(p.renewalOf && byId.has(p.renewalOf)));

  return (
    <Page
      title="Profitability"
      actions={
        <>
          <Dropdown
            aria-label="Scenario"
            value={scenario ? scenario.name : "Current plan (status defaults)"}
            selectedOptions={[scenarioId]}
            onOptionSelect={(_, x) => open(x.optionValue ?? LIVE)}
          >
            <Option value={LIVE}>Current plan (status defaults)</Option>
            {d.scenarios.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Dropdown>
          {canSave && scenario && (
            <Button disabled={busy || !changed} onClick={() => void save(false)}>
              Save
            </Button>
          )}
          {canSave && (
            <Button appearance={scenario ? "secondary" : "primary"} disabled={busy} onClick={() => setNaming(scenario ? `${scenario.name} (copy)` : "")}>
              Save as scenario…
            </Button>
          )}
          {canSave && scenario && (
            <Button appearance="subtle" disabled={busy} onClick={() => void remove()}>
              Delete
            </Button>
          )}
          {changed && (
            <Button appearance="subtle" onClick={() => open(scenarioId)}>
              Reset
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        <ErrorBar message={error} />
        <div className="toolbar" aria-label="What the months count">
          <label className="inline-field">
            <Caption1>From</Caption1>
            <Input type="month" value={from} onChange={(_, x) => /^\d{4}-\d{2}$/.test(x.value) && setFrom(x.value)} aria-label="First month" />
          </label>
          <Button size="small" appearance="subtle" onClick={() => setFrom(addMonths(from, -3))}>
            ◀ 3 months
          </Button>
          <Button size="small" appearance="subtle" onClick={() => setFrom(addMonths(from, 3))}>
            3 months ▶
          </Button>
          <TabList size="small" selectedValue={choices.basis} onTabSelect={(_, x) => set({ basis: x.value as "base" | "ote" })} aria-label="Rates">
            <Tab value="base">Base rates</Tab>
            <Tab value="ote">OTE rates</Tab>
          </TabList>
          <Switch label="Weight open projects by probability" checked={choices.weighted} onChange={(_, x) => set({ weighted: x.checked })} />
        </div>

        <div className="kpis" aria-label="Next 12 months">
          <Kpi label="Revenue" value={money(t.revenue)} sub={span} />
          <Kpi label="Costs" value={money(t.costs)} sub="People, project costs and shared costs" />
          <Kpi label="Profit" value={money(t.profit)} tone={t.profit >= 0 ? "good" : "bad"} flag={t.profit >= 0 ? "Profitable" : "Loss"} sub={t.margin === null ? undefined : `${pct(t.margin)} margin`} />
          <Kpi
            label="Months at a loss"
            value={String(result.lossMonths.length)}
            tone={result.lossMonths.length ? "bad" : "good"}
            flag={result.lossMonths.length ? result.lossMonths.join(", ") : "None"}
          />
          <Kpi
            label="Months needing a hire"
            value={String(hireMonths.length)}
            tone={hireMonths.length ? "bad" : "good"}
            flag={hireMonths.length ? `From ${hireMonths[0].month}` : "None"}
            sub={t.capacity ? `${pct(t.demand / t.capacity, 0)} of capacity planned` : undefined}
          />
        </div>

        <Card className="panel wide">
          <div className="panel-head">
            <Subtitle2>Profit by month, {span}</Subtitle2>
            <Button size="small" appearance="subtle" onClick={() => setTable(!table)}>
              {table ? "Show chart" : "Show as a table"}
            </Button>
          </div>
          {table ? (
            <ProfitTable months={result.months} sharedFromActuals={d.sharedFromActuals} />
          ) : (
            <>
              <ProfitLegend />
              <ProfitChart months={result.months} selected={month} onSelect={setMonth} />
            </>
          )}
          <Caption1 className="muted">
            Revenue in the month it is earned. Costs are everyone's pay (salaried pay whether staffed or not, hourly pay for planned hours), project costs, unfilled roles at their standard rate,
            placeholder hires and shared costs. Every month shows the plan; actuals are on Performance.{table ? "" : " Click a month to break it down below."}
          </Caption1>
        </Card>

        <Breakdown key={month ?? ""} months={result.months} month={month && d.months.includes(month) ? month : null} onMonth={setMonth} />

        <Card className="panel wide">
          <div className="panel-head">
            <Subtitle2>Projects: switch them on or off</Subtitle2>
            <div className="toolbar">
              <Button size="small" appearance="subtle" onClick={() => set({ on: Object.fromEntries(d.projects.map((p) => [p.id, true])) })}>
                All on
              </Button>
              <Button size="small" appearance="subtle" onClick={() => set({ on: {} })}>
                Status defaults
              </Button>
            </div>
          </div>
          <Caption1 className="muted">
            Booked and pending start on; opportunities and prospects start off. <em>Adds to profit</em> is what the project changes in the window: its revenue less its project costs, hourly pay and
            unfilled roles. Salaried pay is already counted, so staffing a project with salaried people adds its whole revenue.
          </Caption1>
          {result.renewalsToConsider.length > 0 && (
            <Warnings
              intent="info"
              items={[
                `${result.renewalsToConsider.length === 1 ? "One booked project ends" : `${result.renewalsToConsider.length} booked projects end`} in the window with no renewal: ${result.renewalsToConsider
                  .map((id) => byId.get(id)?.code || byId.get(id)?.name)
                  .join(", ")}. Use Potential renewal… beside ${result.renewalsToConsider.length === 1 ? "it" : "each"} to see what renewing ${result.renewalsToConsider.length === 1 ? "it" : "them"} would do.`,
              ]}
            />
          )}
          <div className="table-scroll">
            <table className="month-table" aria-label="Projects and whether they count">
              <thead>
                <tr>
                  <th>Counts</th>
                  <th>Project</th>
                  <th>Status</th>
                  <th>Ends</th>
                  <th className="num">Revenue in window</th>
                  <th className="num">Adds to profit</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {PROFIT_STATUSES.flatMap((s) =>
                  topLevel(s).flatMap((p) => [
                    projectRow(p),
                    ...renewalsOf(p.id).map((r) => projectRow(r, true)),
                  ]),
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <StaffingTable months={result.months} roleName={roleName} />
        <HiresPanel hires={choices.hires} roles={d.roles} months={d.months} hireRates={d.hireRates} basis={choices.basis} onChange={(hires) => set({ hires })} />
      </div>
      {naming !== null && (
        <Dialog open onOpenChange={(_, x) => !x.open && setNaming(null)}>
          <DialogSurface>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save(true, naming);
              }}
            >
              <DialogBody>
                <DialogTitle>Save as a scenario</DialogTitle>
                <DialogContent>
                  <Field label="Name" required hint="Saves which projects are on, the weighting, the rates and the hires. Projects themselves are not changed.">
                    <Input value={naming} maxLength={80} autoFocus onChange={(_, x) => setNaming(x.value)} />
                  </Field>
                  <ErrorBar message={error} />
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => setNaming(null)}>
                    Cancel
                  </Button>
                  <Button appearance="primary" type="submit" disabled={busy || !naming.trim()}>
                    {busy ? "Saving…" : "Save"}
                  </Button>
                </DialogActions>
              </DialogBody>
            </form>
          </DialogSurface>
        </Dialog>
      )}
      {renewing && (
        <CopyDialog
          source={{ id: renewing.id, name: renewing.name, isTemplate: false, clientName: renewing.clientName, billable: renewing.billable, plan: { startMonday: renewing.start, weeks: renewing.weeks, assignments: [], schedule: [], expenses: [] } }}
          renewal
          codes={codes.data?.codes ?? []}
          onClose={() => setRenewing(null)}
          onCreated={(id) => {
            setRenewing(null);
            go("projects", id);
          }}
        />
      )}
    </Page>
  );

  function projectRow(p: ProfitProject, renewal = false) {
    const on = isOn(p, choices);
    const x = impact.get(p.id)!;
    const unrenewed = result!.renewalsToConsider.includes(p.id);
    return (
      <tr key={p.id} className={on ? undefined : "muted"}>
        <td>
          <Switch aria-label={`Count ${p.code || p.name}`} checked={on} onChange={(_, s) => toggle(p, s.checked)} />
        </td>
        <td>
          {renewal && <span aria-hidden="true">↳ </span>}
          <a href={href("projects", p.id)}>{p.code ? `${p.code} — ${p.name}` : p.name}</a>
          {renewal && <Caption1 className="muted"> potential renewal</Caption1>}
          {p.clientName && <Caption1 className="muted"> · {p.clientName}</Caption1>}
        </td>
        <td>
          {STATUS_LABEL[p.status]}
          {p.status !== "booked" ? ` · ${pct(p.probability, 0)}` : ""}
        </td>
        <td>{p.end}</td>
        <td className="num">{money(x.revenue)}</td>
        <td className="num">{money(x.profit)}</td>
        <td>
          {canSave && p.status === "booked" && (
            <Tooltip content="Copy this project as an opportunity starting when it ends, linked to it here." relationship="description">
              <Button size="small" appearance={unrenewed ? "secondary" : "subtle"} onClick={() => setRenewing(p)}>
                Potential renewal…
              </Button>
            </Tooltip>
          )}
        </td>
      </tr>
    );
  }
}

const GROUP_LABEL: Record<ProfitLineGroup, string> = {
  revenue: "Revenue",
  projectCosts: "Project costs",
  fixedPeople: "Salaried and monthly people",
  hourlyPeople: "Hourly people",
  unfilled: "Unfilled roles",
  hires: "Placeholder hires",
  shared: "Shared costs",
};

const sumOf = (xs: readonly ComparedLine[], f: (l: ComparedLine) => number) => xs.reduce((t, l) => t + f(l), 0);
const tone = (n: number) => (Math.abs(n) < 0.5 ? "muted" : n > 0 ? "delta-good" : "delta-bad");

/**
 * What makes up revenue and costs, line by line (PF8): the 12 months, or the month picked on the
 * chart against another month (the one before, by default), biggest changes first in each group.
 */
function Breakdown({ months, month, onMonth }: { months: ProfitMonth[]; month: string | null; onMonth: (m: string | null) => void }) {
  // undefined: compare with the month before; "": compare with nothing; else that month.
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const i = month ? months.findIndex((m) => m.month === month) : -1;
  const compareMonth = i < 0 ? null : picked === undefined || picked === month ? (months[i - 1]?.month ?? null) : picked || null;
  const shown = i < 0 ? months : [months[i]];
  const against = compareMonth ? months.filter((m) => m.month === compareMonth) : [];
  const comparing = against.length > 0;
  const lines = compareLines(shown, against);
  const profitNow = shown.reduce((t, m) => t + m.profit, 0);
  const profitBefore = against.reduce((t, m) => t + m.profit, 0);
  const drivers = lines
    .filter((l) => Math.abs(l.effect) >= 0.5)
    .sort((a, b) => Math.abs(b.effect) - Math.abs(a.effect))
    .slice(0, 3);
  const title = i < 0 ? `the 12 months, ${months[0]?.month} – ${months.at(-1)?.month}` : month!;
  const link = (l: ComparedLine) => (l.projectId ? href("projects", l.projectId) : l.personId ? href("people", l.personId) : null);

  const cells = (xs: readonly ComparedLine[]) => {
    const effect = sumOf(xs, (l) => l.effect);
    return (
      <>
        <td className="num">{money(sumOf(xs, (l) => l.amount))}</td>
        {comparing && <td className="num">{money(sumOf(xs, (l) => l.before))}</td>}
        {comparing && <td className={`num ${tone(effect)}`}>{delta(effect, money)}</td>}
      </>
    );
  };
  const section = (group: ProfitLineGroup) => {
    const rows = lines.filter((l) => l.group === group).sort((a, b) => (comparing ? Math.abs(b.effect) - Math.abs(a.effect) : b.amount - a.amount));
    if (!rows.length) return [];
    return [
      <tr key={group} className="group-row">
        <th scope="rowgroup">{GROUP_LABEL[group]}</th>
        {cells(rows)}
      </tr>,
      ...rows.map((l) => {
        const to = link(l);
        return (
          <tr key={l.key}>
            <th scope="row" className="indent">
              {to ? <a href={to}>{l.label}</a> : l.label}
            </th>
            {cells([l])}
          </tr>
        );
      }),
    ];
  };
  const revenue = lines.filter((l) => l.group === "revenue");
  const costs = lines.filter((l) => l.group !== "revenue");

  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>What's in {title}</Subtitle2>
        {i >= 0 && (
          <div className="toolbar">
            <label className="inline-field">
              <Caption1>Compare with</Caption1>
              <Select aria-label="Compare with" value={compareMonth ?? ""} onChange={(_, x) => setPicked(x.value)}>
                <option value="">Nothing</option>
                {months
                  .filter((m) => m.month !== month)
                  .map((m) => (
                    <option key={m.month} value={m.month}>
                      {m.month}
                    </option>
                  ))}
              </Select>
            </label>
            <Button
              size="small"
              appearance="subtle"
              onClick={() => {
                setPicked(undefined);
                onMonth(null);
              }}
            >
              All 12 months
            </Button>
          </div>
        )}
      </div>
      {i < 0 ? (
        <Caption1 className="muted">Click a month on Profit by month to see just that month, and what changed from the month before.</Caption1>
      ) : (
        <Body1>
          {profitNow >= 0 ? "Profit" : "Loss"} of <strong>{money(Math.abs(profitNow))}</strong> in {month}
          {comparing ? (
            <>
              , against {profitBefore >= 0 ? "a profit" : "a loss"} of {money(Math.abs(profitBefore))} in {compareMonth}: <span className={tone(profitNow - profitBefore)}>{delta(profitNow - profitBefore, money)}</span>.
              {drivers.length > 0 && " Biggest changes: "}
              {drivers.map((l, n) => (
                <span key={l.key}>
                  {n ? "; " : ""}
                  {l.label} ({GROUP_LABEL[l.group].toLowerCase()}) <span className={tone(l.effect)}>{delta(l.effect, money)}</span>
                </span>
              ))}
              {drivers.length > 0 && "."}
            </>
          ) : (
            "."
          )}
        </Body1>
      )}
      <div className="table-scroll">
        <table className="month-table breakdown" aria-label={`What's in ${title}`}>
          <thead>
            <tr>
              <th>Source</th>
              <th className="num">{i < 0 ? "12 months" : month}</th>
              {comparing && <th className="num">{compareMonth}</th>}
              {comparing && <th className="num">Effect on profit</th>}
            </tr>
          </thead>
          <tbody>
            {section("revenue")}
            <tr className="total-row">
              <th scope="row">Total revenue</th>
              {cells(revenue)}
            </tr>
          </tbody>
          <tbody>
            {COST_GROUPS.flatMap(section)}
            <tr className="total-row">
              <th scope="row">Total costs</th>
              {cells(costs)}
            </tr>
          </tbody>
          <tbody>
            <tr className="total-row">
              <th scope="row">Profit</th>
              <td className="num">{money(profitNow)}</td>
              {comparing && <td className="num">{money(profitBefore)}</td>}
              {comparing && <td className={`num ${tone(profitNow - profitBefore)}`}>{delta(profitNow - profitBefore, money)}</td>}
            </tr>
          </tbody>
        </table>
      </div>
      {comparing && <Caption1 className="muted">Effect on profit: more revenue, or less cost, is positive. In each group the biggest effects come first.</Caption1>}
    </Card>
  );
}

/** The months as a profit and loss: one row per line, one column per month, totals at the right. */
function ProfitTable({ months, sharedFromActuals }: { months: ProfitMonth[]; sharedFromActuals: boolean[] }) {
  const lines: [string, (m: ProfitMonth) => number, boolean?][] = [
    ["Revenue", (m) => m.revenue, true],
    ["Project costs", (m) => m.projectCosts],
    ["Salaried and monthly people", (m) => m.fixedPeople],
    ["Hourly people", (m) => m.hourlyPeople],
    ["Unfilled roles", (m) => m.unfilled],
    ["Placeholder hires", (m) => m.hires],
    ["Shared costs", (m) => m.shared],
    ["Costs", (m) => m.costs, true],
    ["Profit", (m) => m.profit, true],
  ];
  const total = (f: (m: ProfitMonth) => number) => months.reduce((s, m) => s + f(m), 0);
  const revenue = total((m) => m.revenue);
  return (
    <div className="table-scroll">
      <table className="month-table" aria-label="Profit and loss by month">
        <thead>
          <tr>
            <th />
            {months.map((m) => (
              <th key={m.month} className="num">
                {m.month}
              </th>
            ))}
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map(([name, f, strong]) => (
            <tr key={name}>
              <th scope="row">{strong ? <strong>{name}</strong> : name}</th>
              {months.map((m, i) => (
                <td key={m.month} className="num">
                  {money(f(m))}
                  {name === "Shared costs" && sharedFromActuals[i] ? "*" : ""}
                </td>
              ))}
              <td className="num">{strong ? <strong>{money(total(f))}</strong> : money(total(f))}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">Margin</th>
            {months.map((m) => (
              <td key={m.month} className="num">
                {m.margin === null ? "—" : pct(m.margin)}
              </td>
            ))}
            <td className="num">{revenue ? pct(total((m) => m.profit) / revenue) : "—"}</td>
          </tr>
          <tr>
            <th scope="row">Cumulative profit</th>
            {months.map((m) => (
              <td key={m.month} className="num">
                {money(m.cumulative)}
              </td>
            ))}
            <td />
          </tr>
        </tbody>
      </table>
      {sharedFromActuals.some(Boolean) && <Caption1 className="muted">* Shared costs from the month's actuals; other months are the year's budget ÷ 12.</Caption1>}
    </div>
  );
}

/** Capacity against planned hours by month, who is over, and unfilled hours by role (PF4). */
function StaffingTable({ months, roleName }: { months: ProfitMonth[]; roleName: (id: string) => string }) {
  const roles = [...new Set(months.flatMap((m) => Object.keys(m.unfilledHours)))];
  return (
    <Card className="panel wide">
      <Subtitle2>Staffing by month</Subtitle2>
      <Caption1 className="muted">
        Hours the switched-on projects plan, against capacity (less approved time off, plus hires). <strong>Hire</strong>: the team is over, or a role's unfilled hours exceed its people's spare hours. <strong>Move work</strong>: someone is
        over while others have room, or unfilled hours fit people in the role. <strong>Bench</strong>: the team is under.
      </Caption1>
      <div className="table-scroll">
        <table className="month-table" aria-label="Staffing by month">
          <thead>
            <tr>
              <th />
              {months.map((m) => (
                <th key={m.month} className="num">
                  {m.month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Signal</th>
              {months.map((m) => (
                <td key={m.month} className={`num ${m.flag ? FLAG_CLASS[m.flag] : ""}`}>
                  {m.flag ? FLAG_TEXT[m.flag] : ""}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Planned</th>
              {months.map((m) => (
                <td key={m.month} className="num">
                  {fmtHours(m.demand)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Capacity</th>
              {months.map((m) => (
                <td key={m.month} className="num">
                  {fmtHours(m.capacity)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Utilization</th>
              {months.map((m) => (
                <td key={m.month} className="num">
                  {m.utilization === null ? "no cap." : pct(m.utilization, 0)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Over</th>
              {months.map((m) => (
                <td key={m.month} className="num" title={m.over.map((o) => `${o.name} ${o.load === null ? "no capacity" : pct(o.load, 0)}`).join(", ")}>
                  {m.over.map((o) => o.name.split(" ")[0]).join(", ")}
                </td>
              ))}
            </tr>
            {roles.map((r) => (
              <tr key={r}>
                <th scope="row">Unfilled: {roleName(r)}</th>
                {months.map((m) => (
                  <td key={m.month} className="num">
                    {m.unfilledHours[r] ? fmtHours(m.unfilledHours[r]) : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Placeholder hires (PF5): a role, a start month and hours a week, costed at the role's target salary. */
function HiresPanel(props: {
  hires: PlaceholderHire[];
  roles: ProfitabilityResponse["roles"];
  months: string[];
  hireRates: ProfitabilityResponse["hireRates"];
  basis: "base" | "ote";
  onChange: (hires: PlaceholderHire[]) => void;
}) {
  const change = (id: string, patch: Partial<PlaceholderHire>) => props.onChange(props.hires.map((h) => (h.id === id ? { ...h, ...patch } : h)));
  const monthly = (h: PlaceholderHire) => {
    const r = props.hireRates[h.roleId];
    const target = (props.basis === "base" ? r?.targetBase : r?.targetOte)?.[0];
    return target != null && r.targetHoursPerWeek ? { amount: target * (h.hoursPerWeek / r.targetHoursPerWeek), source: "target salary" } : null;
  };
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>Placeholder hires</Subtitle2>
        <Button
          size="small"
          disabled={!props.roles.length}
          onClick={() => props.onChange([...props.hires, { id: newId(), roleId: props.roles[0].id, start: props.months[0], hoursPerWeek: 40 }])}
        >
          Add a hire
        </Button>
      </div>
      <Caption1 className="muted">
        A hire costs the role's target salary, loaded (Settings → Roles), or its standard rate without one. Its hours first fill unfilled hours of its role, which then stop costing at the standard
        rate.
      </Caption1>
      {props.hires.length === 0 ? (
        <Body1 className="muted">No hires in this view.</Body1>
      ) : (
        <table className="month-table" aria-label="Placeholder hires">
          <thead>
            <tr>
              <th>Role</th>
              <th>Starts</th>
              <th className="num">Hours a week</th>
              <th className="num">Cost a month</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.hires.map((h) => {
              const m = monthly(h);
              return (
                <tr key={h.id}>
                  <td>
                    <Select aria-label="Role" value={h.roleId} onChange={(_, x) => change(h.id, { roleId: x.value })}>
                      {props.roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>
                    <Input type="month" aria-label="Start month" value={h.start} onChange={(_, x) => /^\d{4}-\d{2}$/.test(x.value) && change(h.id, { start: x.value })} />
                  </td>
                  <td className="num">
                    <Input
                      type="number"
                      aria-label="Hours a week"
                      min={1}
                      max={60}
                      value={String(h.hoursPerWeek)}
                      onChange={(_, x) => {
                        const v = Number(x.value);
                        if (v > 0 && v <= 60) change(h.id, { hoursPerWeek: v });
                      }}
                    />
                  </td>
                  <td className="num">{m ? `${money(m.amount)} (${m.source})` : "Standard rate × hours"}</td>
                  <td>
                    <Button size="small" appearance="subtle" onClick={() => props.onChange(props.hires.filter((x) => x.id !== h.id))}>
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
